import { useEffect, useMemo, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { SectionCard } from "@/components/common/SectionCard";
import { usePagination } from "@/components/common/usePagination";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  EMPTY_COMPANY_RATE,
  useManufacturingLaborCompanyRates,
  type CompanyRateDraft,
} from "./useManufacturingLaborCompanyRates";
import { ManufacturingLaborCompanyRateForm } from "./ManufacturingLaborCompanyRateForm";
import { ManufacturingLaborCompanyRateKindMenu } from "./ManufacturingLaborCompanyRateKindMenu";
import { ManufacturingLaborCompanyRateImportModal } from "./ManufacturingLaborCompanyRateImportModal";
import { ManufacturingLaborCompanyRateUnmatched } from "./ManufacturingLaborCompanyRateUnmatched";
import { listManufacturingRecipes } from "@/lib/manufacturing/recipesClient";
import type { MfgRecipeKind, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import { laborRateImportTemplateCsv } from "@/lib/manufacturing/laborCompanyRateImport";
import {
  applyRecipeToCompanyRateDraft,
  defaultLaborRateRecipeKinds,
  filterRecipesForLaborRateMatch,
  parseLaborRateRecipeKinds,
} from "@/lib/manufacturing/laborCompanyRateRecipe";
import { ManufacturingLaborCompanyRateGaps } from "./ManufacturingLaborCompanyRateGaps";
import { LABOR_PAY_DEFAULT_PAGE_SIZE, LABOR_PAY_PAGE_SIZES } from "@/lib/manufacturing/laborPayPaging";
import { laborPaysByShort } from "@/lib/manufacturing/laborPayQuantity";

const KIND_STORAGE_KEY = "mfg:labor-company-rate-kinds:v1";

function readStoredKinds(): Set<MfgRecipeKind> {
  try {
    return parseLaborRateRecipeKinds(JSON.parse(localStorage.getItem(KIND_STORAGE_KEY) ?? ""));
  } catch {
    return defaultLaborRateRecipeKinds();
  }
}

function money(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function ManufacturingLaborCompanyRates({
  enabled,
  navTick,
  seed,
  onSeedConsumed,
  onSaved,
  readOnly = false,
  search = "",
  embedded = false,
}: {
  enabled: boolean;
  navTick: number;
  seed?: CompanyRateDraft | null;
  onSeedConsumed?: () => void;
  onSaved?: () => void;
  readOnly?: boolean;
  search?: string;
  embedded?: boolean;
}) {
  const s = useManufacturingLaborCompanyRates(enabled, navTick);
  const [recipes, setRecipes] = useState<MfgRecipeSummary[]>([]);
  const [kinds, setKinds] = useState<Set<MfgRecipeKind>>(readStoredKinds);
  const [importOpen, setImportOpen] = useState(false);
  const q = search.trim().toLowerCase();
  const filtered = q
    ? s.items.filter((item) =>
        [item.label, item.option_code, item.item_code, item.notes].join(" ").toLowerCase().includes(q)
      )
    : s.items;
  const matched = filtered.filter((item) => item.recipe_id && item.pays_by);
  const page = usePagination(matched, LABOR_PAY_DEFAULT_PAGE_SIZE);
  const catalog = useMemo(() => filterRecipesForLaborRateMatch(recipes, kinds), [kinds, recipes]);
  const importCatalog = useMemo(() => recipes.filter((r) => r.is_active), [recipes]);
  const searchable = useMemo(
    () => filterRecipesForLaborRateMatch(recipes, kinds, s.draft.recipe_id),
    [kinds, recipes, s.draft.recipe_id]
  );

  function changeKinds(next: Set<MfgRecipeKind>) {
    setKinds(next);
    localStorage.setItem(KIND_STORAGE_KEY, JSON.stringify([...next]));
  }

  useEffect(() => {
    if (!enabled) return;
    void listManufacturingRecipes().then((res) => {
      if (res.ok) setRecipes(res.data.recipes);
    });
  }, [enabled, navTick]);

  useEffect(() => {
    if (!seed || readOnly) return;
    s.startCreate(seed);
    onSeedConsumed?.();
  }, [seed, readOnly]);

  const ratesBody = (
    <>
      {s.error ? <p className="mb-2 text-sm text-red-700 dark:text-red-300">{s.error}</p> : null}
      {readOnly ? null : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={ghostBtnClass}
              disabled={s.actionBusy}
              onClick={() => s.startCreate()}
            >
              + New rate
            </button>
            <ManufacturingLaborCompanyRateKindMenu
              kinds={kinds}
              onChange={changeKinds}
              disabled={s.actionBusy}
            />
            <button
              type="button"
              className={ghostBtnClass}
              disabled={s.actionBusy || catalog.length === 0}
              onClick={() =>
                downloadCsv(
                  "labor-rates-template.csv",
                  laborRateImportTemplateCsv(catalog, s.items)
                )
              }
            >
              Download
            </button>
            <button
              type="button"
              className={ghostBtnClass}
              disabled={s.actionBusy}
              onClick={() => setImportOpen(true)}
            >
              Upload
            </button>
          </div>
          {s.showForm ? (
            <ManufacturingLaborCompanyRateForm
              draft={s.draft}
              setDraft={s.setDraft}
              editing={Boolean(s.editingId)}
              busy={s.actionBusy}
              recipes={searchable}
              placeholder={
                kinds.size === 1 && kinds.has("product")
                  ? "Search product SKUs…"
                  : "Search Models recipes…"
              }
              onSave={() =>
                void s.submit().then((ok) => {
                  if (ok) onSaved?.();
                })
              }
              onCancel={s.cancel}
            />
          ) : null}
        </>
      )}
      {importOpen && !readOnly ? (
        <ManufacturingLaborCompanyRateImportModal
          recipes={importCatalog}
          existing={s.items}
          busy={s.actionBusy}
          onSaveAccepted={(rows) => s.saveImported(rows)}
          onClose={() => setImportOpen(false)}
          onImported={() => {
            setImportOpen(false);
            onSaved?.();
          }}
        />
      ) : null}
      <ManufacturingLaborCompanyRateGaps
        recipes={recipes}
        rates={s.items}
        search={search}
        readOnly={readOnly}
        busy={s.actionBusy}
        onAdd={(recipe) =>
          s.startCreate(applyRecipeToCompanyRateDraft({ ...EMPTY_COMPANY_RATE }, recipe))
        }
      />
      {s.listBusy && s.items.length === 0 ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : matched.length === 0 ? (
        <p className="text-sm text-gray-500">
          {s.items.length === 0
            ? readOnly
              ? "No company rates yet."
              : "No matched company rates yet. Add one by picking a product SKU from Models, or upload a rate file."
            : "No matched rates for that search."}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
          {page.paginatedItems.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <div className="font-medium text-gray-900 dark:text-zinc-100">{item.label}</div>
                <div className="text-xs text-gray-500 dark:text-zinc-400">
                  <code>{item.option_code || item.item_code}</code> · {money(item.flat_rate_usd)} /{" "}
                  {laborPaysByShort(item.pays_by)}
                  {!item.is_active ? " · inactive" : ""}
                </div>
              </div>
              {readOnly ? null : (
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" className={ghostBtnClass} disabled={s.actionBusy} onClick={() => s.startEdit(item)}>
                    Edit
                  </button>
                  <button type="button" className={ghostBtnClass} disabled={s.actionBusy} onClick={() => void s.toggleActive(item)}>
                    {item.is_active ? "Deactivate" : "Activate"}
                  </button>
                  <button type="button" className={ghostBtnClass} disabled={s.actionBusy} onClick={() => void s.remove(item.id)}>
                    Remove
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <PaginationBar
        pageIndex={page.pageIndex}
        pageCount={page.pageCount}
        pageSize={page.pageSize}
        totalCount={page.totalCount}
        firstShown={page.firstShown}
        lastShown={page.lastShown}
        onPageChange={page.setPageIndex}
        onPageSizeChange={page.setPageSize}
        pageSizeOptions={LABOR_PAY_PAGE_SIZES}
        noun="rates"
        alwaysShow
      />
    </>
  );

  return (
    <>
      <ManufacturingLaborCompanyRateUnmatched
        items={s.items}
        recipes={recipes}
        busy={s.actionBusy}
        readOnly={readOnly}
        search={search}
        onResolved={() => {
          void s.reload();
          onSaved?.();
        }}
      />
      {embedded ? (
        ratesBody
      ) : (
        <SectionCard
          title="Company labor rates"
          description={
            readOnly
              ? "What the labor company is paid per order item. Pays by is required (as ordered, each, or a building measure). Enumerated $0 options are included in the building or package."
              : "Pick a Models SKU, set the $ rate, and choose Pays by. Do not assume quantity 1. $0 on an IdeaRoom line means included — it is not a separate pay line."
          }
        >
          {ratesBody}
        </SectionCard>
      )}
    </>
  );
}
